<template>
  <q-btn
    no-caps
    color="grey-5"
    :bg-color="!$q.dark.isActive ? 'white' : 'black'"
    outline
    class="q-py-sm"
    data-html2canvas-ignore
    :disabled="(options.length === 0 && !optionsFunction) || loading"
  >
    <div
      style="font-weight: 400; font-size: small"
      :class="!$q.dark.isActive ? 'text-black' : 'text-white'"
    >
      {{ label }}
      {{
        includedOptions.length || excludedOptions.length
          ? ` (${includedOptions.length + excludedOptions.length})`
          : ''
      }}
    </div>
    <q-menu flat max-width="600px" transition-duration="100" @before-show="loadOptions">
      <q-card flat style="min-width: 250px">
        <q-card-section class="q-px-sm q-pt-none q-pb-md">
          <div class="q-mb-sm q-px-md q-pt-md">
            <div class="row items-center">
              <span class="title"> {{ title }} </span>
              <div>
                <q-icon name="info_outline" class="q-ml-xs" size="xs" />
                <q-tooltip v-if="tooltip">
                  {{ tooltip }}
                </q-tooltip>
              </div>
            </div>
            <div
              v-if="filterHelpText.plainText"
              class="row items-center"
              :class="
                includedOptions.length > 0 || excludedOptions.length > 0
                  ? 'help-text-card'
                  : 'instructions-card'
              "
            >
              <q-tooltip
                v-if="includedOptions.length > 0 || excludedOptions.length > 0"
                class="text-body2"
                max-width="250px"
              >
                {{ filterHelpText.plainText }}
                <b>
                  {{ filterHelpText.styledText }}
                </b>
              </q-tooltip>
              <p class="q-ma-none text-caption help-text">
                {{ filterHelpText.plainText }}
                <b>
                  {{ filterHelpText.styledText }}
                </b>
              </p>
            </div>
          </div>
          <div v-if="loading" class="q-pa-md">
            <q-linear-progress indeterminate color="primary" class="q-mt-md" />
          </div>
          <q-list v-else dense>
            <q-item
              v-for="option in sortedOptions"
              :key="option.value"
              class="row items-center button-trigger"
            >
              <q-btn-toggle
                :model-value="optionValue(option)"
                :options="[
                  {
                    slot: 'include',
                    value: true
                  },
                  {
                    slot: 'exclude',
                    value: false
                  }
                ]"
                style="outline: 1px solid grey"
                class="q-mr-sm"
                size="sm"
                clearable
                dense
                unelevated
                @update:model-value="handleOptionsUpdate(option, $event)"
              >
                <template #include>
                  <q-icon name="check" />
                  <q-tooltip :delay="800"> Include amenity </q-tooltip>
                </template>
                <template #exclude>
                  <q-icon name="close" />
                  <q-tooltip :delay="800"> Exclude amenity </q-tooltip>
                </template>
              </q-btn-toggle>
              {{ option.label }} ({{ option.count }})
              <q-btn
                flat
                dense
                color="primary"
                class="visible-on-hover q-ml-md q-mr-sm q-my-none"
                style="min-width: 40px; font-size: 12px; line-height: 1"
                @click="selectOnly(option)"
              >
                Only
              </q-btn>
            </q-item>
          </q-list>
        </q-card-section>
        <div style="position: sticky; bottom: 0; z-index: 1; background-color: inherit">
          <q-separator />
          <q-card-actions align="right">
            <q-btn unelevated dense label="None" no-caps @click="setNone" />
            <q-btn unelevated dense label="All" no-caps @click="setAll" />
            <q-btn
              v-close-popup
              unelevated
              dense
              label="Apply"
              color="primary"
              no-caps
              @click="applyOptions"
            />
          </q-card-actions>
        </div>
      </q-card>
    </q-menu>
  </q-btn>
</template>

<script lang="ts">
import type { Option } from '@/components/SelectOptions.vue'
import type { PropType } from 'vue'

export default {
  name: 'IncludeExcludeSelectOptions',
  props: {
    options: {
      type: Array as PropType<Option[]>,
      required: false,
      default: () => []
    },
    optionsFunction: {
      type: Function as PropType<() => Option[]>,
      required: false,
      default: null
    },
    label: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    tooltip: {
      type: String,
      required: false,
      default: ''
    },
    aliases: {
      type: Object as PropType<Record<string, string>>,
      required: false,
      default: () => ({})
    }
  },
  emits: ['apply'],
  data() {
    return {
      includedOptions: [] as Option[],
      excludedOptions: [] as Option[],
      loadedOptions: [] as Option[],
      loading: false
    }
  },
  computed: {
    sortedOptions(): Option[] {
      return this.loadedOptions
        .toSorted((a, b) => a.value.localeCompare(b.value))
        .map((option) => {
          const alias = this.aliases[option.value]
          return {
            ...option,
            label: alias || option.label
          }
        })
    },
    filterHelpText(): { plainText: string; styledText: string } {
      const helpText = { plainText: '', styledText: '' }
      if (this.includedOptions.length === 0 && this.excludedOptions.length === 0) {
        helpText.plainText = 'Select amenities that a unit must or must not have.'
        return helpText
      }
      if (this.includedOptions.length) {
        helpText.plainText =
          'Showing units that have ' + this.combineWords(this.includedOptions.map((_) => _.label))
      }
      if (this.includedOptions.length && this.excludedOptions.length) {
        helpText.plainText =
          'Showing units that have ' + this.combineWords(this.includedOptions.map((_) => _.label))
        helpText.styledText =
          ' but do not have ' +
          this.combineWords(
            this.excludedOptions.map((_) => _.label),
            'nor'
          )
        return helpText
      } else if (this.excludedOptions.length) {
        helpText.plainText =
          'Showing units that do not have ' +
          this.combineWords(
            this.excludedOptions.map((_) => _.label),
            'nor'
          )
      }
      return helpText
    }
  },
  watch: {
    options: {
      handler(newOptions: Option[]) {
        if (newOptions.length > 0 && !this.optionsFunction) {
          this.loadedOptions = newOptions
        }
      },
      immediate: true
    }
  },
  methods: {
    loadOptions() {
      if (this.optionsFunction) {
        this.loading = true
        try {
          // Use nextTick to ensure the menu is shown before potentially heavy computation
          this.$nextTick(() => {
            const result = this.optionsFunction()
            this.loadedOptions = result
            this.loading = false
          })
        } catch (error) {
          console.error('Error loading options:', error)
          this.loading = false
        }
      } else if (this.options.length > 0) {
        this.loadedOptions = this.options
      }
    },
    combineWords(words: string[], combiner: string = 'and') {
      if (words.length === 1) {
        return words[0]
      }
      if (words.length === 2) {
        return words[0] + ' ' + combiner + ' ' + words[1]
      }
      const lastWord = words.pop()
      return words.join(', ') + ', ' + combiner + ' ' + lastWord
    },
    optionValue(option: Option): boolean | null {
      if (this.includedOptions.some((_) => _.value === option.value)) {
        return true
      }
      if (this.excludedOptions.some((_) => _.value === option.value)) {
        return false
      }
      return null
    },
    setNone() {
      this.includedOptions = []
      this.excludedOptions = []
      this.applyOptions()
    },
    setAll() {
      if (this.includedOptions.length === this.loadedOptions.length) {
        // all is already set
        return
      }
      this.includedOptions = []
      this.excludedOptions = []
      for (const option in this.loadedOptions) {
        this.includedOptions.push(this.loadedOptions[option])
      }
      this.applyOptions()
    },
    handleOptionsUpdate(option: Option, event: boolean) {
      if (event === null) {
        if (this.optionValue(option)) {
          this.includedOptions.splice(
            this.includedOptions.findIndex((a) => a.label === option.label),
            1
          )
        }
        if (this.optionValue(option) === false) {
          this.excludedOptions.splice(
            this.excludedOptions.findIndex((a) => a.label === option.label),
            1
          )
        }
      } else if (event) {
        if (this.optionValue(option) === false) {
          this.excludedOptions.splice(
            this.excludedOptions.findIndex((a) => a.label === option.label),
            1
          )
        }
        this.includedOptions.push(option)
      } else {
        if (this.optionValue(option)) {
          this.includedOptions.splice(
            this.includedOptions.findIndex((a) => a.label === option.label),
            1
          )
        }
        this.excludedOptions.push(option)
      }
    },
    applyOptions() {
      this.$emit('apply', { include: this.includedOptions, exclude: this.excludedOptions })
    },
    selectOnly(option: Option) {
      this.excludedOptions = []
      this.includedOptions = []
      this.includedOptions.push(option)
      this.applyOptions()
    }
  }
}
</script>

<style scoped lang="scss">
.title {
  font-size: 17px;
  font-weight: 500;
}
.visible-on-hover {
  opacity: 0;
}
.button-trigger:hover .visible-on-hover {
  opacity: 1;
}
.body--dark {
  .help-text-card {
    border-left: $blue-4 solid 2px;
    background-color: $blue-10;
    color: $blue-1;
  }
  .instructions-card {
    border-left: $grey-5 solid 2px;
    background-color: $grey-9;
    color: $grey-2;
  }
}
.help-text-card {
  border-left: $blue-4 solid 2px;
  background-color: $blue-1;
  color: $blue-9;
  padding: 4px;
  margin-top: 2px;
}
.instructions-card {
  border-left: $grey-5 solid 2px;
  background-color: $grey-2;
  color: $grey-9;
  padding: 4px;
  margin-top: 2px;
}
.help-text {
  display: -webkit-box;
  min-height: 2rem;
  max-width: 250px;
  line-height: 1rem;
  text-overflow: ellipsis;
  overflow: hidden;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
</style>
